import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router'

import { MCP_AUTH_LANDING_PATH } from '@ti/contracts'
import { AuthSession } from '@ti/core/auth/services'

import { isMcpRequestId } from '../state/mcp-attempt.store'
import { injectMcpLoginAsCheck } from './mcp-login-as'

/**
 * The sign-in return target is rebuilt from the validated request id alone: the router URL could
 * carry arbitrary extra parameters or a fragment, and nothing but the handle may ride along
 * through the sign-in page, its history entry and telemetry.
 */
function landingReturnUrl(route: ActivatedRouteSnapshot): string {
  const requestIds = route.queryParamMap.getAll('request')
  return requestIds.length === 1 && isMcpRequestId(requestIds[0])
    ? `${MCP_AUTH_LANDING_PATH}?request=${requestIds[0]}`
    : MCP_AUTH_LANDING_PATH
}

/**
 * Deliberately not `loggedInGuard`: that one calls the is-active API and signs the user out (wiping
 * session storage, and with it the pending attempt) on any failure of it, and returns false instead
 * of a redirect that keeps the return target. This landing needs three things only, in this order:
 * Login as never reaches a token or the API, a signed-out visitor is sent to sign-in and brought
 * back, and everything else is the page's business, with retry and restart states of its own.
 */
export const mcpAuthorizeGuard: CanActivateFn = async (route) => {
  const isLoginAsActive = injectMcpLoginAsCheck()
  const router = inject(Router)
  const authSession = inject(AuthSession)

  if (isLoginAsActive()) {
    return true
  }
  // A failed session read is "not signed in", which is recoverable at the sign-in page; it must
  // not become a rejected navigation, and certainly not a sign-out.
  const signedIn = await authSession.isLoggedIn().catch(() => false)
  if (signedIn) {
    return true
  }
  return router.createUrlTree(['/sign-in'], { queryParams: { returnUrl: landingReturnUrl(route) } })
}
